import React, { useCallback, useRef, useState } from "react";

/**
 * Side-by-side comparison slider — drag the divider to reveal "before" vs "after".
 *
 * Props:
 *  - before: data URL (original)
 *  - after: data URL (enhanced)
 *  - height: optional max-height (default '60vh')
 */
export default function BeforeAfterSlider({ before, after, height = "60vh" }) {
  const containerRef = useRef(null);
  const [pos, setPos] = useState(50); // % from start (RTL = from right)
  const [dragging, setDragging] = useState(false);

  const updateFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    // In RTL, 0% = right edge, 100% = left edge. Compute distance from right.
    const distFromRight = rect.right - clientX;
    const pct = Math.max(0, Math.min(100, (distFromRight / rect.width) * 100));
    setPos(pct);
  }, []);

  const handlePointerDown = (e) => {
    setDragging(true);
    e.currentTarget.setPointerCapture?.(e.pointerId);
    updateFromClientX(e.clientX);
  };
  const handlePointerMove = (e) => {
    if (!dragging) return;
    updateFromClientX(e.clientX);
  };
  const handlePointerUp = (e) => {
    setDragging(false);
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full bg-[#1A1C19] overflow-hidden select-none touch-none"
      style={{ maxHeight: height }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      data-testid="before-after-slider"
    >
      {/* After image (full visible) */}
      <img
        src={after}
        alt="بعد التحسين"
        className="block w-full h-auto select-none pointer-events-none"
        draggable={false}
        style={{ maxHeight: height, objectFit: "contain", margin: "0 auto" }}
      />

      {/* Before image clipped from the right (RTL) */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ clipPath: `inset(0 0 0 ${100 - pos}%)` }}
        aria-hidden="true"
      >
        <img
          src={before}
          alt=""
          className="block w-full h-auto"
          draggable={false}
          style={{ maxHeight: height, objectFit: "contain", margin: "0 auto" }}
        />
      </div>

      {/* Divider line */}
      <div
        className="absolute top-0 bottom-0 w-px bg-white/85 pointer-events-none"
        style={{ right: `${pos}%` }}
        aria-hidden="true"
      >
        {/* Handle */}
        <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-9 h-9 rounded-full bg-white border-2 border-[#1F3A33] flex items-center justify-center shadow-3d">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1F3A33" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
            <polyline points="9 18 15 12 9 6" transform="translate(6,0)" />
          </svg>
        </div>
      </div>

      {/* Labels */}
      <div className="absolute top-3 end-3 px-2.5 py-1 rounded-full bg-black/50 text-white text-xs font-readex backdrop-blur-sm">
        قبل
      </div>
      <div className="absolute top-3 start-3 px-2.5 py-1 rounded-full bg-[#1F3A33]/85 text-white text-xs font-readex backdrop-blur-sm">
        بعد
      </div>
    </div>
  );
}
